import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Calendar,
  MapPin,
  Plus,
  Edit,
  Trash2,
  Users,
  CheckCircle,
  Camera,
  ExternalLink,
  X,
  Download,
} from "lucide-react";

const STATUS_OPTIONS = [
  { value: "upcoming", label: "Yaklaşan" },
  { value: "active", label: "Aktif (bugün)" },
  { value: "completed", label: "Tamamlandı" },
  { value: "cancelled", label: "İptal Edildi" },
];

const statusLabel = (s) => STATUS_OPTIONS.find((o) => o.value === s)?.label || s;
const statusClass = (s) => {
  if (s === "upcoming") return "bg-primary/15 text-primary";
  if (s === "active") return "bg-amber-100 text-amber-800";
  if (s === "completed") return "bg-emerald-100 text-emerald-800";
  if (s === "cancelled") return "bg-rose-100 text-rose-800";
  return "bg-muted";
};

const formatDateTR = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("tr-TR", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso;
  }
};

export default function AdoptionEventsPanel() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/adoption-events`);
      setEvents(r.data.events || []);
    } catch (e) {
      toast.error("Etkinlikler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Bu etkinliği silmek istediğine emin misin?")) return;
    try {
      await axios.delete(`${API}/admin/adoption-events/${id}`);
      toast.success("Etkinlik silindi");
      loadEvents();
    } catch {
      toast.error("Silinemedi");
    }
  };

  const upcoming = useMemo(
    () => events.filter((e) => ["upcoming", "active"].includes(e.status)),
    [events]
  );
  const past = useMemo(
    () => events.filter((e) => ["completed", "cancelled"].includes(e.status)),
    [events]
  );

  return (
    <div className="space-y-8" data-testid="adoption-events-panel">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl tracking-tight">Sahiplendirme Etkinlikleri</h1>
          <p className="text-muted-foreground mt-1">
            Pativista Sahiplendirme Günü — KAHU destekli. Her etkinliği yönet ve sonrasını raporla.
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="btn-pill bg-primary text-primary-foreground" data-testid="create-event-btn">
              <Plus className="w-4 h-4 mr-2" />
              Yeni Etkinlik
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader className="sticky -top-6 -mt-6 pt-6 pb-3 bg-background z-10 border-b border-border/50">
              <DialogTitle>Yeni Sahiplendirme Etkinliği</DialogTitle>
            </DialogHeader>
            <EventForm
              onSaved={() => {
                setCreateOpen(false);
                loadEvents();
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-16 text-muted-foreground">Yükleniyor…</div>
      ) : (
        <>
          <Section title="Yaklaşan / Aktif" icon={Calendar} count={upcoming.length}>
            {upcoming.length === 0 ? (
              <EmptyHint />
            ) : (
              upcoming.map((e) => (
                <EventRow
                  key={e.event_id}
                  event={e}
                  onEdit={() => setEditingId(e.event_id)}
                  onDelete={() => handleDelete(e.event_id)}
                />
              ))
            )}
          </Section>

          <Section title="Geçmiş Etkinlikler" icon={CheckCircle} count={past.length}>
            {past.length === 0 ? (
              <p className="text-sm text-muted-foreground">Henüz tamamlanmış etkinlik yok.</p>
            ) : (
              past.map((e) => (
                <EventRow
                  key={e.event_id}
                  event={e}
                  onEdit={() => setEditingId(e.event_id)}
                  onDelete={() => handleDelete(e.event_id)}
                />
              ))
            )}
          </Section>
        </>
      )}

      {editingId && (
        <Dialog open={!!editingId} onOpenChange={(o) => !o && setEditingId(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader className="sticky -top-6 -mt-6 pt-6 pb-3 bg-background z-10 border-b border-border/50">
              <DialogTitle>Etkinliği Yönet</DialogTitle>
            </DialogHeader>
            <EventDetailEditor
              eventId={editingId}
              onSaved={() => {
                setEditingId(null);
                loadEvents();
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, count, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-5 h-5 text-primary" strokeWidth={1.5} />
        <h2 className="font-heading text-xl">{title}</h2>
        <Badge variant="outline" className="ml-2">
          {count}
        </Badge>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function EmptyHint() {
  return (
    <div className="text-sm text-muted-foreground border border-dashed rounded-xl p-6 text-center">
      Henüz yaklaşan etkinlik yok. <span className="text-foreground font-medium">"Yeni Etkinlik"</span> ile başla.
    </div>
  );
}

function EventRow({ event, onEdit, onDelete }) {
  return (
    <Card className="bg-card border border-border/50" data-testid={`event-row-${event.event_id}`}>
      <CardContent className="p-5 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <Badge className={statusClass(event.status)}>{statusLabel(event.status)}</Badge>
            <h3 className="font-heading text-lg truncate">{event.title}</h3>
            {event.subtitle && (
              <span className="text-xs text-muted-foreground">— {event.subtitle}</span>
            )}
          </div>
          <div className="text-sm text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              {formatDateTR(event.event_date)} · {event.start_time}–{event.end_time}
            </span>
            <span className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" />
              {event.location_name}, {event.location_city}
            </span>
            <span className="flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" />
              {event.featured_dogs_count} can
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={async (e) => {
              e.stopPropagation();
              try {
                const r = await axios.get(
                  `${API}/admin/adoption-events/${event.event_id}/report-pdf`,
                  { responseType: "blob" }
                );
                const blob = new Blob([r.data], { type: "application/pdf" });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                const safeTitle = (event.title || "etkinlik").replace(/[^A-Za-z0-9_-]+/g, "_");
                const safeDate = (event.event_date || "").replace(/-/g, "");
                a.download = `kahu-${safeTitle}-${safeDate}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
              } catch {
                toast.error("Rapor indirilemedi");
              }
            }}
            data-testid={`download-event-${event.event_id}`}
          >
            <Download className="w-3.5 h-3.5" />
          </Button>
          <Button variant="outline" size="sm" onClick={onEdit} data-testid={`edit-event-${event.event_id}`}>
            <Edit className="w-3.5 h-3.5 mr-1" />
            Düzenle
          </Button>
          <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700" onClick={onDelete}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function EventForm({ initial, eventId, onSaved }) {
  const [form, setForm] = useState({
    title: initial?.title || "Pativista Sahiplendirme Günü",
    title_en: initial?.title_en || "",
    subtitle: initial?.subtitle || "KAHU destekli",
    subtitle_en: initial?.subtitle_en || "",
    event_date: initial?.event_date || "",
    start_time: initial?.start_time || "11:00",
    end_time: initial?.end_time || "16:00",
    location_name: initial?.location_name || "Kuğulu Park",
    location_name_en: initial?.location_name_en || "",
    location_city: initial?.location_city || "Ankara",
    location_address:
      initial?.location_address || "Kuğulu Park, Tunalı Hilmi Cd., Çankaya / Ankara",
    location_address_en: initial?.location_address_en || "",
    map_url:
      initial?.map_url || "https://maps.google.com/?q=Ku%C4%9Fulu+Park+Ankara",
    description: initial?.description || "",
    description_en: initial?.description_en || "",
    status: initial?.status || "upcoming",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.event_date) {
      toast.error("Etkinlik tarihi gerekli");
      return;
    }
    setSaving(true);
    try {
      if (eventId) {
        await axios.put(`${API}/admin/adoption-events/${eventId}`, form);
        toast.success("Etkinlik güncellendi");
      } else {
        await axios.post(`${API}/admin/adoption-events`, form);
        toast.success("Etkinlik oluşturuldu");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };


  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label>Başlık</Label>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <div className="mt-1.5 flex items-center gap-2">
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 shrink-0">EN</span>
            <Input
              value={form.title_en}
              onChange={(e) => setForm({ ...form, title_en: e.target.value })}
              placeholder="Title (English)"
              className="text-sm"
              data-testid="event-title-en-input"
            />
          </div>
        </div>
        <div>
          <Label>Alt başlık (co-branding)</Label>
          <Input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} />
          <div className="mt-1.5 flex items-center gap-2">
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 shrink-0">EN</span>
            <Input
              value={form.subtitle_en}
              onChange={(e) => setForm({ ...form, subtitle_en: e.target.value })}
              placeholder="Subtitle (English)"
              className="text-sm"
              data-testid="event-subtitle-en-input"
            />
          </div>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <Label>Tarih</Label>
          <Input
            type="date"
            value={form.event_date}
            onChange={(e) => setForm({ ...form, event_date: e.target.value })}
            data-testid="event-date-input"
          />
        </div>
        <div>
          <Label>Başlangıç</Label>
          <Input
            type="time"
            value={form.start_time}
            onChange={(e) => setForm({ ...form, start_time: e.target.value })}
          />
        </div>
        <div>
          <Label>Bitiş</Label>
          <Input
            type="time"
            value={form.end_time}
            onChange={(e) => setForm({ ...form, end_time: e.target.value })}
          />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label>Lokasyon Adı</Label>
          <Input
            value={form.location_name}
            onChange={(e) => setForm({ ...form, location_name: e.target.value })}
          />
          <div className="mt-1.5 flex items-center gap-2">
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 shrink-0">EN</span>
            <Input
              value={form.location_name_en}
              onChange={(e) => setForm({ ...form, location_name_en: e.target.value })}
              placeholder="Venue name (English)"
              className="text-sm"
              data-testid="event-location-en-input"
            />
          </div>
        </div>
        <div>
          <Label>Şehir</Label>
          <Input
            value={form.location_city}
            onChange={(e) => setForm({ ...form, location_city: e.target.value })}
          />
        </div>
      </div>
      <div>
        <Label>Adres</Label>
        <Input
          value={form.location_address}
          onChange={(e) => setForm({ ...form, location_address: e.target.value })}
        />
        <div className="mt-1.5 flex items-center gap-2">
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 shrink-0">EN</span>
          <Input
            value={form.location_address_en}
            onChange={(e) => setForm({ ...form, location_address_en: e.target.value })}
            placeholder="Address (English)"
            className="text-sm"
            data-testid="event-address-en-input"
          />
        </div>
      </div>
      <div>
        <Label>Harita Linki (Google Maps)</Label>
        <Input
          value={form.map_url}
          onChange={(e) => setForm({ ...form, map_url: e.target.value })}
          placeholder="https://maps.google.com/?q=..."
        />
      </div>
      <div>
        <Label>Açıklama / Duyuru Metni</Label>
        <Textarea
          rows={3}
          placeholder="Bu Pazar Kuğulu Park'ta seni bekliyoruz! 20+ can sahiplenilmeyi bekliyor…"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
        <div className="mt-1.5 flex items-start gap-2">
          <span className="text-[10px] font-mono px-1.5 py-0.5 mt-2 rounded bg-blue-100 text-blue-700 shrink-0">EN</span>
          <Textarea
            rows={3}
            placeholder="Announcement (English)"
            value={form.description_en}
            onChange={(e) => setForm({ ...form, description_en: e.target.value })}
            className="text-sm"
            data-testid="event-description-en-input"
          />
        </div>
      </div>
      <div>
        <Label>Durum</Label>
        <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button onClick={handleSave} disabled={saving} data-testid="save-event-btn">
          {saving ? "Kaydediliyor…" : "Kaydet"}
        </Button>
      </div>
    </div>
  );
}

function EventDetailEditor({ eventId, onSaved }) {
  const [event, setEvent] = useState(null);
  const [suggested, setSuggested] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recap, setRecap] = useState({
    visitors_estimated: 0,
    adoptions_count: 0,
    new_applications: 0,
    donations_total: 0,
    notes: "",
  });
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [evRes, sugRes] = await Promise.all([
        axios.get(`${API}/admin/adoption-events/${eventId}`),
        axios.get(`${API}/admin/adoption-events/featured-dogs/suggest?limit=40`),
      ]);
      setEvent(evRes.data);
      setSuggested(sugRes.data.dogs || []);
      const r = evRes.data.recap || {};
      setRecap({
        visitors_estimated: r.visitors_estimated || 0,
        adoptions_count: r.adoptions_count || 0,
        new_applications: r.new_applications || 0,
        donations_total: r.donations_total || 0,
        notes: r.notes || "",
      });
    } catch {
      toast.error("Etkinlik yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const featuredIds = event?.featured_dog_ids || [];
  const isFeatured = (id) => featuredIds.includes(id);

  const toggleDog = async (dog_id) => {
    if (!event) return;
    const next = isFeatured(dog_id)
      ? featuredIds.filter((d) => d !== dog_id)
      : [...featuredIds, dog_id];
    try {
      const r = await axios.put(`${API}/admin/adoption-events/${eventId}`, {
        featured_dog_ids: next,
      });
      setEvent(r.data);
    } catch {
      toast.error("Güncellenemedi");
    }
  };


  const saveRecap = async () => {
    try {
      const r = await axios.put(`${API}/admin/adoption-events/${eventId}`, {
        recap: {
          visitors_estimated: Number(recap.visitors_estimated) || 0,
          adoptions_count: Number(recap.adoptions_count) || 0,
          new_applications: Number(recap.new_applications) || 0,
          donations_total: Number(recap.donations_total) || 0,
          notes: recap.notes,
        },
      });
      setEvent(r.data);
      toast.success("Etkinlik raporu kaydedildi");
    } catch {
      toast.error("Kaydedilemedi");
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/admin/adoption-events/${eventId}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEvent((prev) => ({ ...prev, recap: { ...prev.recap, photos: r.data.photos } }));
      toast.success("Fotoğraf yüklendi");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Yüklenemedi");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handlePhotoDelete = async (url) => {
    try {
      const r = await axios.delete(
        `${API}/admin/adoption-events/${eventId}/photos?url=${encodeURIComponent(url)}`
      );
      setEvent((prev) => ({ ...prev, recap: { ...prev.recap, photos: r.data.photos } }));
    } catch {
      toast.error("Silinemedi");
    }
  };

  if (loading || !event) {
    return <div className="py-8 text-center text-muted-foreground">Yükleniyor…</div>;
  }

  return (
    <div className="space-y-8" data-testid="event-detail-editor">
      <div>
        <h3 className="font-heading text-lg mb-3">Etkinlik Bilgileri</h3>
        <EventForm
          initial={event}
          eventId={event.event_id}
          onSaved={() => {
            load();
            onSaved?.();
          }}
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-heading text-lg">Etkinliğe Katılacak Canlar</h3>
          <div className="flex items-center gap-2">
            <Badge variant="outline">{featuredIds.length} seçili</Badge>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-72 overflow-y-auto p-1">
          {suggested.map((d) => {
            const on = isFeatured(d.dog_id);
            return (
              <button
                key={d.dog_id}
                type="button"
                onClick={() => toggleDog(d.dog_id)}
                className={`text-left rounded-xl border p-2 transition-all ${
                  on
                    ? "border-primary bg-primary/10"
                    : "border-border hover:border-primary/50"
                }`}
                data-testid={`toggle-dog-${d.dog_id}`}
              >
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-lg bg-muted overflow-hidden flex-shrink-0">
                    {d.photo_url ? (
                      <img src={d.photo_url} alt={d.name} className="w-full h-full object-cover" />
                    ) : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{d.name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {d.breed || "Karışık"}
                    </p>
                  </div>
                  {on && <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />}
                </div>
              </button>
            );
          })}
        </div>
        {/* Already-featured but not in suggestion list */}
        {event.featured_dogs?.some((d) => !suggested.find((s) => s.dog_id === d.dog_id)) && (
          <div className="mt-3">
            <p className="text-xs text-muted-foreground mb-2">
              Listede seçili ama önerilerde olmayan canlar:
            </p>
            <div className="flex flex-wrap gap-2">
              {event.featured_dogs
                ?.filter((d) => !suggested.find((s) => s.dog_id === d.dog_id))
                .map((d) => (
                  <button
                    key={d.dog_id}
                    type="button"
                    onClick={() => toggleDog(d.dog_id)}
                    className="text-xs px-3 py-1 rounded-full bg-primary/10 text-primary flex items-center gap-1"
                  >
                    {d.name}
                    <X className="w-3 h-3" />
                  </button>
                ))}
            </div>
          </div>
        )}
      </div>

      <div>
        <h3 className="font-heading text-lg mb-3">Etkinlik Sonrası Raporu</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>Tahmini Ziyaretçi</Label>
            <Input
              type="number"
              value={recap.visitors_estimated}
              onChange={(e) => setRecap({ ...recap, visitors_estimated: e.target.value })}
            />
          </div>
          <div>
            <Label>Yuva Bulan Can Sayısı</Label>
            <Input
              type="number"
              value={recap.adoptions_count}
              onChange={(e) => setRecap({ ...recap, adoptions_count: e.target.value })}
              data-testid="recap-adoptions-input"
            />
          </div>
          <div>
            <Label>Başlatılan Başvuru</Label>
            <Input
              type="number"
              value={recap.new_applications}
              onChange={(e) => setRecap({ ...recap, new_applications: e.target.value })}
            />
          </div>
          <div>
            <Label>Toplanan Bağış (TL)</Label>
            <Input
              type="number"
              step="0.01"
              value={recap.donations_total}
              onChange={(e) => setRecap({ ...recap, donations_total: e.target.value })}
            />
          </div>
        </div>
        <div className="mt-3">
          <Label>Notlar / Hatıralar</Label>
          <Textarea
            rows={3}
            placeholder="Çocukların yüzlerindeki ışıltı, yeni ailesini bulan Çakıl…"
            value={recap.notes}
            onChange={(e) => setRecap({ ...recap, notes: e.target.value })}
          />
        </div>
        <div className="flex justify-end mt-3 gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const r = await axios.get(
                  `${API}/admin/adoption-events/${eventId}/report-pdf`,
                  { responseType: "blob" }
                );
                const blob = new Blob([r.data], { type: "application/pdf" });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                const safeTitle = (event.title || "etkinlik").replace(/[^A-Za-z0-9_-]+/g, "_");
                const safeDate = (event.event_date || "").replace(/-/g, "");
                a.download = `kahu-${safeTitle}-${safeDate}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                toast.success("Rapor indirildi");
              } catch (err) {
                toast.error("Rapor indirilemedi");
              }
            }}
            data-testid="download-event-report-btn"
          >
            <Download className="w-4 h-4 mr-1" />
            PDF İndir
          </Button>
          <Button onClick={saveRecap} data-testid="save-recap-btn">
            Raporu Kaydet
          </Button>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg">Etkinlik Fotoğrafları</h3>
          <label className="cursor-pointer">
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              className="hidden"
              disabled={uploading}
            />
            <span className="btn-pill bg-primary text-primary-foreground px-4 py-2 text-sm inline-flex items-center gap-1.5">
              <Camera className="w-3.5 h-3.5" />
              {uploading ? "Yükleniyor…" : "Fotoğraf Ekle"}
            </span>
          </label>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {(event.recap?.photos || []).map((url, i) => (
            <div key={`${url}-${i}`} className="relative aspect-square rounded-xl overflow-hidden bg-muted group">
              <img
                src={url.startsWith("http") ? url : `${API.replace(/\/api$/, "")}${url}`}
                alt={`Etkinlik foto ${i + 1}`}
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => handlePhotoDelete(url)}
                className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          {(event.recap?.photos || []).length === 0 && (
            <p className="text-sm text-muted-foreground col-span-full text-center py-6 border border-dashed rounded-xl">
              Henüz fotoğraf yok. Etkinlik sonrası fotoğrafları paylaş.
            </p>
          )}
        </div>
      </div>

      {event.map_url && (
        <a
          href={event.map_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-primary inline-flex items-center gap-1 hover:underline"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          Google Maps'te aç
        </a>
      )}
    </div>
  );
}
